/**
 * AlwaysVisibleActions Component
 *
 * Renders bulk action buttons that are ALWAYS visible in the toolbar.
 * Unlike BulkLifecycleToolbarContent which only shows when rows are selected,
 * this component shows all actions at all times with visual states:
 *
 * - No selection: Ghost/disabled (40% opacity)
 * - Selection, 0 applicable: Disabled, no count
 * - Selection with applicable: Enabled with count badge
 *
 * This eliminates "mode confusion" by showing all available actions at all times.
 *
 * For 'activate' actions, supports a special unified activation flow that shows
 * supersession previews and allows opt-out before execution.
 */

import { Button, Tooltip } from 'antd'
import type { BulkAction } from './BulkLifecycleToolbarContent'
import { renderIcon, getDisabledTooltip, showConfirmThenExecute } from './actionUtils'

/**
 * Props for AlwaysVisibleActions component
 */
export interface AlwaysVisibleActionsProps {
  /** Number of selected rows */
  selectedCount: number
  /** All available bulk actions (lifecycle + record) */
  actions: BulkAction[]
  /** Called to execute a bulk action */
  onExecute: (actionKey: string) => Promise<void>
  /** Whether an operation is currently running */
  isExecuting?: boolean
  /** Entity label for messages (e.g., 'offerings', 'customers') */
  entityLabel?: string
  /**
   * Optional callback for 'activate' action with unified preview flow.
   * When provided, 'activate' clicks call this instead of showing Modal.confirm.
   * This enables the supersession-aware activation flow.
   */
  onActivateWithPreview?: () => void
}

/**
 * AlwaysVisibleActions - Always-visible bulk action buttons
 *
 * Shows all actions at all times with appropriate visual states.
 * Ghost when no selection, disabled when no applicable items, enabled with counts otherwise.
 *
 * @example
 * ```tsx
 * <AlwaysVisibleActions
 *   selectedCount={5}
 *   actions={allBulkActions}
 *   onExecute={handleBulkExecute}
 *   entityLabel="offerings"
 * />
 * ```
 */
export function AlwaysVisibleActions({
  selectedCount,
  actions,
  onExecute,
  isExecuting = false,
  entityLabel = 'items',
  onActivateWithPreview,
}: AlwaysVisibleActionsProps) {
  // Sort actions by order (don't filter - show all)
  const sortedActions = [...actions].sort((a, b) => (a.order ?? 50) - (b.order ?? 50))

  return (
    <div className="always-visible-actions">
      {sortedActions.map((action) => {
        // Determine visual state
        const isDisabled = selectedCount === 0 || action.applicableCount === 0
        const isGhost = selectedCount === 0
        const showCount = selectedCount > 0 && action.applicableCount > 0

        // Build button content
        const buttonContent = (
          <Button
            icon={renderIcon(action.icon)}
            type={action.buttonType}
            danger={action.danger}
            disabled={isDisabled || isExecuting}
            loading={isExecuting}
            className={`action-btn ${isGhost ? 'action-btn-ghost' : ''}`}
          >
            <span className="toolbar-btn-text">{action.label}</span>
            {showCount && <span className="action-count"> ({action.applicableCount})</span>}
          </Button>
        )

        // If disabled, wrap in tooltip explaining why
        if (isDisabled) {
          const tooltipText = getDisabledTooltip(action, selectedCount, entityLabel)
          return (
            <Tooltip key={action.key} title={tooltipText}>
              {/* Wrapper span needed for tooltip on disabled button */}
              <span className="action-btn-wrapper">{buttonContent}</span>
            </Tooltip>
          )
        }

        // If enabled, wrap in tooltip (for icon-only mode at narrow widths)
        // Click shows Modal.confirm for action confirmation
        // Tooltip shows "Activate (5)" when CSS hides button text
        const enabledTooltipText = `${action.label} (${action.applicableCount})`

        const handleClick = () => {
          // For 'activate' action with preview callback, use the unified flow
          if (action.key === 'activate' && onActivateWithPreview) {
            onActivateWithPreview()
            return
          }

          // Default: confirmation modal with proper afterClose handling
          // Uses showConfirmThenExecute to ensure confirmation modal is FULLY closed
          // (animation complete) before progress modal appears
          showConfirmThenExecute(action, entityLabel, () => onExecute(action.key))
        }

        return (
          <Tooltip key={action.key} title={enabledTooltipText}>
            <Button
              icon={renderIcon(action.icon)}
              type={action.buttonType}
              danger={action.danger}
              disabled={isDisabled || isExecuting}
              loading={isExecuting}
              className={`action-btn ${isGhost ? 'action-btn-ghost' : ''}`}
              onClick={handleClick}
            >
              <span className="toolbar-btn-text">{action.label}</span>
              {showCount && <span className="action-count"> ({action.applicableCount})</span>}
            </Button>
          </Tooltip>
        )
      })}
    </div>
  )
}
